/**
 * URL scrubbing for analytics — strips product/category handles from URLs
 * when health consent is not granted.
 *
 * Product and category pages use root-level URLs (`/sildenafil-50mg`,
 * `/erectile-dysfunction`) which reveal health conditions. When health
 * consent is not granted, these are replaced with a generic placeholder.
 */

import { SAFE_PATH_PREFIXES, SCRUBBED_PATH } from './constants';

/**
 * Returns true if the pathname could be a product or category page
 * (i.e. its first segment is not a known safe route).
 */
export const isHealthSensitivePath = (pathname: string): boolean => {
  // Strip leading slash and get first segment
  const firstSegment = pathname.replace(/^\/+/, '').split('/')[0];

  // Empty = homepage, always safe
  if (!firstSegment) {
    return false;
  }

  return !SAFE_PATH_PREFIXES.has(firstSegment);
};

/**
 * Scrub a URL string, replacing the path with SCRUBBED_PATH if it's
 * potentially a product/category page.
 */
export const scrubUrlIfHealthSensitive = (url: string): string => {
  try {
    const parsed = new URL(url);

    if (isHealthSensitivePath(parsed.pathname)) {
      parsed.pathname = SCRUBBED_PATH;
      parsed.search = '';
      return parsed.toString();
    }

    return url;
  } catch {
    // Not a valid absolute URL — strip query/hash before checking the path
    const bare = url.split(/[?#]/)[0];
    if (isHealthSensitivePath(bare)) {
      return SCRUBBED_PATH;
    }

    return url;
  }
};

/**
 * Scrub a pathname, replacing it with SCRUBBED_PATH if it's potentially
 * a product/category page.
 */
export const scrubPathnameIfHealthSensitive = (pathname: string): string => {
  if (isHealthSensitivePath(pathname)) {
    return SCRUBBED_PATH;
  }

  return pathname;
};
